/*
  The chapters index (CP-17): the pure parts. A chapter is a piece of the specification that people write in sections; its
  key is the short name in its address (`/chapters/<key>`), which the server holds to lower-case letters, digits and
  underscores, starting with a letter. The app only suggests a key from the title; the server makes the real check.
*/

import type { ChapterView } from "../../../api/types";

/** The longest key the app suggests. */
const MAX_KEY = 60;

/**
 * A key from a title: "Shaft power rules" becomes "shaft_power_rules". A title that begins with a digit gets "chapter_" in front,
 * because a key must begin with a letter. A title with no letter or digit in it gives no key (the empty text).
 */
export function chapterKeyFromTitle(title: string): string {
  const snake = title
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!snake) return "";
  const key = /^[a-z]/.test(snake) ? snake : `chapter_${snake}`;
  return key.slice(0, MAX_KEY).replace(/_+$/, "");
}

export interface SectionCounts {
  total: number;
  settled: number;
  draft: number;
  declined: number;
}

/** How many sections of a chapter are settled, still drafts, or declined. */
export function sectionCounts(chapter: Pick<ChapterView, "sections">): SectionCounts {
  const counts: SectionCounts = { total: chapter.sections.length, settled: 0, draft: 0, declined: 0 };
  for (const section of chapter.sections) {
    if (section.status === "settled") counts.settled += 1;
    else if (section.status === "declined") counts.declined += 1;
    else counts.draft += 1;
  }
  return counts;
}

/** "3 sections: 2 settled, 1 draft", or "No section written yet". */
export function sectionWords(counts: SectionCounts): string {
  if (counts.total === 0) return "No section written yet";
  const parts = [
    counts.settled > 0 ? `${counts.settled} settled` : null,
    counts.draft > 0 ? `${counts.draft} ${counts.draft === 1 ? "draft" : "drafts"}` : null,
    counts.declined > 0 ? `${counts.declined} declined` : null,
  ].filter(Boolean);
  return `${counts.total} ${counts.total === 1 ? "section" : "sections"}: ${parts.join(", ")}`;
}

/** The chapters in the order the index shows them: the ones with a draft first (work to do), then by title. */
export function sortChapters(chapters: readonly ChapterView[]): ChapterView[] {
  const open = (chapter: ChapterView) => (sectionCounts(chapter).draft > 0 ? 0 : 1);
  return [...chapters].sort((a, b) => open(a) - open(b) || (a.title || a.chapter_key).localeCompare(b.title || b.chapter_key));
}

/** The address of a chapter's editor. */
export function chapterHref(key: string): string {
  return `/chapters/${encodeURIComponent(key)}`;
}
